import { useCallback, useContext } from 'react';
import { useSettings } from '../contexts/settingsContext';
import { useSearch } from '../contexts/searchContext';
import { useCameraCaptures } from '../contexts/cameraCapturesContext';
import { useHistoryDrawer } from '../contexts/historyDrawerContext';
import { CognitoUserContext } from '../contexts/cognitoUserContext';
import { searchFace } from '../services/api/rekognitionService';
import { getPersonsByIds } from '../services/api/personService';
import { createMatchEvent, createGroupMatchEvent, createDetectionEvent, getCurrentLocation } from '../services/api/eventService';
import { compressImage } from '../services/image/compressionService';
import { getSuggestedCompressionQuality } from '../services/image/networkSpeedService';
import { setLocalStorageEnumMap } from '../services/user/browserService';
import { uploadCapture } from '../services/s3/captureUploadService';

/**
 * Custom hook for face matching logic
 * Handles compression, network-aware quality adjustment, face search, and result processing
 * Supports both interactive (CameraModal) and silent (StealthCamera) modes
 *
 * @param {Object} options Configuration options
 * @param {boolean} options.silent - If true, operates silently without UI updates (for stealth mode)
 * @param {Function} options.onSuccess - Callback on successful match (receives result, matchedPersons)
 * @param {Function} options.onNoMatch - Callback when no match found (receives captureId, dataUrl)
 * @param {Function} options.onError - Callback on error (receives error, captureId)
 * @returns {Object} Face matching functions and state
 */
export function useFaceMatching(options = {}) {
  const { silent = false, onSuccess, onNoMatch, onError } = options;

  const settings = useSettings();
  const { cognitoUser } = useContext(CognitoUserContext);
  const {
    selectedDataset,
    setEnumMap,
    setTriggerAttributeSearch,
    setFilteredPeople,
    setMatchSource,
    setMatchAccuracy,
    setCapturedImageS3Key
  } = useSearch();
  const { updateCapture } = useCameraCaptures();
  const { openAssignments, addPendingMatchEvent } = useHistoryDrawer();

  const {
    sliderValue,
    multiFaceMode,
    compressionQuality,
    setCompressionQuality,
    autoAdjustCompression,
    updateCharacteristicsOnMatch,
    autoCreateEventOnAIMatch,
    isIndoor,
    locationHighAccuracy,
    locationAcquisitionTimeout,
    locationCacheMaxAge
  } = settings;

  /**
   * Performs face matching with network-aware compression
   * @param {string} captureId - ID of the capture to match
   * @param {string} dataUrl - Base64 image data URL
   * @param {Object} clickPosition - Optional click position {x: percentage, y: percentage}
   * @returns {Promise<Object>} Match result with persons and metadata
   */
  const matchFace = useCallback(async (captureId, dataUrl, clickPosition = null) => {
    try {

      // Network-aware compression quality adjustment
      let qualityToUse = compressionQuality;

      if (autoAdjustCompression) {
        qualityToUse = getSuggestedCompressionQuality();
        setCompressionQuality(qualityToUse);
      }

      // Compress image before uploading/matching
      // Two-stage compression strategy:
      // 1. MAX_SIZE_MB: Hard limit on file size (7MB - balances quality vs upload time)
      // 2. qualityToUse: Network-aware initial quality (0.1-1.0, auto-adjusted based on connection speed)
      //    - Fast connection: Start with high quality (0.9), reduce only if needed to meet size limit
      //    - Slow connection: Start with lower quality (0.6-0.8) to reduce upload time
      const MAX_SIZE_MB = 7;
      const compressedDataUrl = await compressImage(dataUrl, MAX_SIZE_MB, qualityToUse);

      // Capture GPS coordinates (always required)
      // - Indoor mode: Used for event creation only (not for proximity analysis)
      // - Outdoor mode: Used for both event creation AND proximity analysis (50m threshold)
      let gpsCoords = null;
      try {
        gpsCoords = await getCurrentLocation({
          locationHighAccuracy,
          locationAcquisitionTimeout,
          locationCacheMaxAge
        });
      } catch (err) {
        console.warn('[Face Match] Failed to get GPS coordinates:', err.message, err);
      }

      // Send base64 to Lambda - Lambda will save to S3 before processing
      // This avoids needing Cognito Identity Pool credentials in the browser
      const userId = cognitoUser?.attributes?.sub || cognitoUser?.username;

      // Get open assignment ID and description from context (client knows the truth!)
      const openAssignment = openAssignments.length > 0 ? openAssignments[0] : null;
      const assignmentEventId = openAssignment?.eventId || null;
      const assignmentDescription = openAssignment?.description || null;

      // Search for matching faces (Lambda saves image to S3 and returns key)
      const result = await searchFace(
        compressedDataUrl, // image (base64)
        sliderValue,
        multiFaceMode, // boolean: false = single face, true = multi face
        null, // imageS3Key (not used - Lambda saves it)
        userId, // userId (Cognito sub)
        gpsCoords, // GPS coordinates (always captured for events; used for proximity if outdoor)
        isIndoor, // Indoor/outdoor flag (determines proximity analysis method)
        'fast', // mode: 'fast' = return known immediately, process unknowns async
        assignmentEventId, // Assignment event ID (for linking match events to open assignments)
        assignmentDescription, // Assignment description (for display in event UI)
        clickPosition // Click position (x%, y%) where user clicked on image
      );

      // Always treat matches as an array (0 or more matches)
      const matches = result.matches || [];
      const matchCount = matches.length;

      // Update capture with success/failure status and all bounding boxes
      updateCapture(captureId, {
        matchFailed: matchCount === 0,
        boundingBoxes: result.detectedFaces?.map(f => f.boundingBox).filter(Boolean) || [], // All detected faces (matched or not)
        boundingBox: matches.length > 0 ? matches[0].boundingBox : null, // Keep first for backward compatibility
        clusterMetadata: result.clusterMetadata || null // V3.2: Cluster metadata for color assignment
      });

      // DISABLED: Client-side detection event creation (now handled serverside by faceMatchEventProcessor)
      // Events are created via EventBridge: faceMatch → EventBridge → faceMatchEventProcessor
      let detectionEventIdPromise = null;

      // Fetch person details (needed for both UI and event creation)
      // Start this immediately but don't wait for interactive mode
      let matchedPersonsPromise = null;
      if (matchCount > 0) {
        // Separate known persons from associated persons
        const knownMatches = matches.filter(m => !m.matchType || m.matchType === 'known');
        const associatedMatches = matches.filter(m => m.matchType === 'associated' || m.matchType === 'associated_new');

        if (knownMatches.length > 0) {
          const personIds = knownMatches.map(m => m.externalImageId);
          const uniquePersonIds = [...new Set(personIds)];
          matchedPersonsPromise = getPersonsByIds(uniquePersonIds).then(persons => {
            return persons;
          }).catch(err => {
            console.error('[Face Match] Failed to fetch person details:', err);
            return [];
          });
        } else {
        }
      } else {
      }

      // For auto-create mode: wait for person details (needed for event creation)
      // For interactive mode: return immediately, fetch in background
      const matchedPersons = (autoCreateEventOnAIMatch && matchedPersonsPromise)
        ? await matchedPersonsPromise
        : [];

      // Build match list function (reusable for sync and async paths)
      const buildMatchList = (persons) => {
        const clusterMetadata = result.clusterMetadata;
        let matchList = '';

        if (clusterMetadata && clusterMetadata.total_clusters > 1) {
          // Build cluster-grouped message from cluster structure
          const clusterMessages = clusterMetadata.clusters
            .sort((a, b) => a.cluster_index - b.cluster_index)
            .map(cluster => {
              const names = cluster.faces
                .filter(face => face.match) // Only faces with matches
                .map(face => {
                  if (face.match.type === 'known') {
                    const person = persons.find(p => p.person_id === face.match.personId);
                    return person ? `${person.first_name || ''} ${person.last_name || ''}`.trim() : face.match.personId;
                  } else {
                    const isNew = face.match.type === 'associated_new';
                    return `Unknown (${isNew ? 'new' : `${face.match.sightingCount || 1}x`})`;
                  }
                });

              return names.length > 0 ? `Group ${cluster.cluster_index + 1}: ${names.join(', ')}` : null;
            })
            .filter(msg => msg !== null);

          matchList = clusterMessages.join(' | ');
        } else {
          // No clustering or single cluster - use simple list
          const knownNames = persons.map(p =>
            `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown'
          );
          const associatedNames = matches
            .filter(m => m.matchType === 'associated' || m.matchType === 'associated_new')
            .map(m => {
              const isNew = m.matchType === 'associated_new';
              return `Unknown (${isNew ? 'new' : `${m.sightingCount || 1}x`})`;
            });
          matchList = [...knownNames, ...associatedNames].join(', ');
        }

        return matchList;
      };

      // Initial match list (may have empty person names if not loaded yet)
      let matchList = buildMatchList(matchedPersons);

      // Interactive mode: update UI contexts (only if NOT auto-creating events)
      if (!silent && !autoCreateEventOnAIMatch && matchCount > 0) {
        // Update UI immediately with what we have
        setFilteredPeople(matchedPersons);
        setMatchSource('face_match');
        setMatchAccuracy(matches[0].similarity / 100); // Use best match
        setCapturedImageS3Key(result.captureS3Key || null);

        // If person details are loading, update UI when they arrive
        if (matchedPersonsPromise && matchedPersons.length === 0) {
          matchedPersonsPromise.then(persons => {
            setFilteredPeople(persons);

            // Update characteristics to match first/best person if enabled
            if (updateCharacteristicsOnMatch && persons.length > 0) {
              const topMatchedPerson = persons[0];

              const newEnumMap = new Map();

              // Map person characteristics to enum map
              if (topMatchedPerson.gender) newEnumMap.set('Gender', topMatchedPerson.gender);
              if (topMatchedPerson.agegroup) newEnumMap.set('AgeGroup', topMatchedPerson.agegroup);
              if (topMatchedPerson.haircolor) newEnumMap.set('HairColor', topMatchedPerson.haircolor);
              if (topMatchedPerson.hairstyle) newEnumMap.set('HairStyle', topMatchedPerson.hairstyle);
              if (topMatchedPerson.facialhair) newEnumMap.set('FacialHair', topMatchedPerson.facialhair);
              if (topMatchedPerson.eyecolor) newEnumMap.set('EyeColor', topMatchedPerson.eyecolor);
              if (topMatchedPerson.skintone) newEnumMap.set('SkinTone', topMatchedPerson.skintone);

              setEnumMap(newEnumMap);
              setLocalStorageEnumMap(newEnumMap);
            }
          });
        } else if (updateCharacteristicsOnMatch && matchedPersons.length > 0) {
          // Person details already loaded
          const topMatchedPerson = matchedPersons[0];

          const newEnumMap = new Map();

          // Map person characteristics to enum map
          if (topMatchedPerson.gender) newEnumMap.set('Gender', topMatchedPerson.gender);
          if (topMatchedPerson.agegroup) newEnumMap.set('AgeGroup', topMatchedPerson.agegroup);
          if (topMatchedPerson.haircolor) newEnumMap.set('HairColor', topMatchedPerson.haircolor);
          if (topMatchedPerson.hairstyle) newEnumMap.set('HairStyle', topMatchedPerson.hairstyle);
          if (topMatchedPerson.facialhair) newEnumMap.set('FacialHair', topMatchedPerson.facialhair);
          if (topMatchedPerson.eyecolor) newEnumMap.set('EyeColor', topMatchedPerson.eyecolor);
          if (topMatchedPerson.skintone) newEnumMap.set('SkinTone', topMatchedPerson.skintone);

          setEnumMap(newEnumMap);
          setLocalStorageEnumMap(newEnumMap);
        }
      }

      // DISABLED: Client-side match event creation (now handled serverside by faceMatchEventProcessor)
      // All event creation (detection, match, group_match) is now done by faceMatchEventProcessor Lambda
      // triggered by EventBridge after faceMatch Lambda completes

      // Add optimistic pending events to show matches immediately in history drawer
      // These will be replaced by real events when serverside processing completes
      if (matchCount > 0 && addPendingMatchEvent) {
        const openAssignment = openAssignments.length > 0 ? openAssignments[0] : null;
        const capturedImageUrl = result.captureS3Key || dataUrl;

        matches.forEach((match, index) => {
          const person = matchedPersons.find(p => p.person_id === match.externalImageId);
          const personName = person
            ? `${person.first_name || ''} ${person.last_name || ''}`.trim() || match.externalImageId
            : match.externalImageId;

          addPendingMatchEvent({
            personName,
            personImage: person?.image || null,
            matchSource: 'face_match',
            matchAccuracy: match.similarity / 100,
            matchType: match.matchType, // Pass matchType so optimistic event knows if it's SOI
            person,
            statementCount: person?.statements?.length || 0,
            openAssignment,
            capturedImageUrl,
            boundingBox: match.boundingBox,
            matchCount,
            detectedFaceCount: result.clusterMetadata?.total_faces_detected || 1,
            location: gpsCoords ? {
              latitude: gpsCoords.latitude,
              longitude: gpsCoords.longitude,
              accuracy: gpsCoords.accuracy,
              timestamp: gpsCoords.timestamp
            } : null
          });
        });
      }

      // Handle no matches - trigger attribute search fallback
      if (matchCount === 0 && !silent && setTriggerAttributeSearch) {
        setTriggerAttributeSearch({ imageDataUrl: dataUrl });
      }

      // Call appropriate callback
      if (matchCount > 0 && onSuccess) {
        onSuccess(result, matchedPersons, matchList);
      } else if (matchCount === 0 && onNoMatch) {
        onNoMatch(captureId, dataUrl);
      }


      return {
        success: matchCount > 0,
        result,
        matchedPersons,
        matchList,
        topMatch: matchCount > 0 ? matches[0] : null
      };
    } catch (err) {
      console.error('Face match error:', err.message);
      updateCapture(captureId, { matchFailed: true });

      // Call error callback
      if (onError) {
        onError(err, captureId);
      }

      throw err;
    }
  }, [
    silent,
    cognitoUser,
    compressionQuality,
    autoAdjustCompression,
    sliderValue,
    multiFaceMode,
    updateCharacteristicsOnMatch,
    autoCreateEventOnAIMatch,
    selectedDataset,
    setCompressionQuality,
    updateCapture,
    setFilteredPeople,
    setMatchSource,
    setMatchAccuracy,
    setEnumMap,
    setTriggerAttributeSearch,
    setCapturedImageS3Key,
    openAssignments,
    addPendingMatchEvent,
    onSuccess,
    onNoMatch,
    onError
  ]);

  return {
    matchFace
  };
}
